import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COMPLIANCE_LABEL, fmtDate } from "@/lib/format";
import {
  Map, Ruler, FileText, Camera, AlertTriangle, ArrowUpRight, Scale, Landmark, Gavel,
} from "lucide-react";

const Stat = ({ label, value, sub, icon: Icon, testid, accent = "text-primary" }) => (
  <div data-testid={testid}
    className="surface p-5 h-full flex flex-col justify-between hover:-translate-y-0.5 hover:shadow-sm transition-transform duration-200">
    <div className="flex items-start justify-between">
      <p className="label-tiny">{label}</p>
      <Icon size={17} className={accent} />
    </div>
    <div className="mt-6">
      <p className="font-display text-3xl md:text-4xl font-bold tracking-tighter">{value}</p>
      {sub && <p className="mt-1.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => setStats(false));
  }, []);

  if (!stats) return <div className="p-8 text-sm text-muted-foreground">Loading overview…</div>;

  const c = stats.compliance;
  const attention = c.overdue + c.never_visited;

  return (
    <div className="p-5 md:p-10 max-w-[1500px] rise" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-tiny">{user?.role === "admin" ? "Portfolio overview" : "My assignments"}</p>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter">
            Welcome, {user?.name?.split(" ")[0]}
          </h1>
        </div>
        <Link to="/parcels" data-testid="view-all-assets-link"
          className="inline-flex items-center gap-2 text-sm font-medium rounded-lg border border-border px-4 py-2.5 bg-card hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors duration-200">
          View all assets <ArrowUpRight size={15} />
        </Link>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Stat testid="stat-parcels" label="Assets" value={stats.total_parcels} icon={Map}
          sub={Object.entries(stats.categories || {}).map(([k, v]) => `${k.slice(0, 4)} ${v}`).join(" · ") || "—"} />
        <Stat testid="stat-acres" label="Extent" value={stats.total_acres} sub="acres monitored" icon={Ruler} />
        <Stat testid="stat-documents" label="Documents" value={stats.documents} sub="in repository" icon={FileText} />
        <Stat testid="stat-visits" label="Visits / month" value={stats.visits_this_month}
          sub={user?.role === "admin" ? `${stats.executives} executive(s)` : "captured by you"} icon={Camera} />
        <Stat testid="stat-cases" label="Open cases" value={stats.open_cases ?? 0} sub="litigation" icon={Scale}
          accent="text-red-500" />
        <Stat testid="stat-mortgages" label="Mortgages" value={stats.active_mortgages ?? 0} sub="active" icon={Landmark}
          accent="text-cyan-500" />
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="surface p-6">
          <div className="flex items-center justify-between">
            <p className="label-tiny">Visit compliance</p>
            {attention > 0 && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-destructive">
                <AlertTriangle size={13} /> {attention} need attention
              </span>
            )}
          </div>
          <div className="mt-6 space-y-3.5" data-testid="compliance-breakdown">
            {["up_to_date", "due", "overdue", "never_visited"].map((k) => {
              const pct = stats.total_parcels ? (c[k] / stats.total_parcels) * 100 : 0;
              const bar =
                k === "up_to_date" ? "bg-emerald-500" : k === "due" ? "bg-amber-500"
                  : k === "overdue" ? "bg-red-500" : "bg-zinc-400";
              return (
                <div key={k} data-testid={`compliance-row-${k}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span>{COMPLIANCE_LABEL[k]}</span>
                    <span className="font-mono text-xs">{c[k]}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full ${bar} transition-[width] duration-500`}
                      style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <Link to="/compliance" data-testid="compliance-report-link"
            className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
            Open compliance report <ArrowUpRight size={13} />
          </Link>
        </div>

        <div className="lg:col-span-2 surface p-6">
          <p className="label-tiny">Latest field captures</p>
          {stats.recent_visits.length === 0 ? (
            <p className="mt-6 text-sm text-muted-foreground">
              No field visits recorded yet. Assign an executive and capture the first update.
            </p>
          ) : (
            <div className="mt-4 divide-y divide-border" data-testid="recent-visits-list">
              {stats.recent_visits.map((v) => (
                <Link to={`/parcels/${v.parcel_id}`} key={v.id} data-testid={`recent-visit-${v.id}`}
                  className="flex items-center justify-between gap-4 py-3 group">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors duration-200">
                      {v.parcel_name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {v.executive_name} · {v.media_count} media · {v.observation}
                    </p>
                  </div>
                  <span className="text-xs font-mono text-muted-foreground whitespace-nowrap">
                    {fmtDate(v.captured_at)}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {stats.upcoming_hearings?.length > 0 && (
        <div className="mt-4 surface p-6" data-testid="upcoming-hearings">
          <p className="label-tiny flex items-center gap-2"><Gavel size={14} className="text-red-500" /> Upcoming court hearings</p>
          <div className="mt-4 divide-y divide-border">
            {stats.upcoming_hearings.map((h, i) => (
              <Link key={i} to={`/parcels/${h.parcel_id}`} data-testid={`hearing-row-${i}`}
                className="flex flex-wrap items-center justify-between gap-3 py-3 group">
                <div>
                  <p className="text-sm font-semibold group-hover:text-primary transition-colors duration-200">
                    {h.parcel_name}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {h.case_number} · {h.court_name || "Court not set"}
                    {h.advocate_name ? ` · Adv. ${h.advocate_name}` : ""}
                  </p>
                </div>
                <span className="text-xs font-mono px-2.5 py-1 rounded-md bg-red-50 text-red-700 border border-red-200">
                  {fmtDate(h.next_hearing_date)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
